import { DatePicker, InputNumber, Radio, theme } from 'antd';
import dayjs from 'dayjs';
import { isEqual } from 'lodash';
import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { selectCredentialByID, selectCredentialLoading } from 'reduxStore/credentials/selectors';
import { credentialsActions } from 'reduxStore/credentials/slice';
import { selectPeople } from 'reduxStore/people/selectors';
import { Button, Flex, Form, Modal, Select, Tooltip, Typography } from 'zui/Antd';

const { Title, Text } = Typography;


const UpdateCredentialModal = ({ open, setOpen, credentialID, children }) => {
	const {
		token: {
			paddingXL,
			fontSizeSM,
			marginLG,
			marginXS,
			colorTextDescription,
			fontWeightStrong,
			colorBorderSecondary,
			paddingSM,
			colorBgLayout
		}
	} = theme.useToken();

	const dispatch = useDispatch();
	const [form] = Form.useForm();

	const credential = useSelector((state) => selectCredentialByID(state, credentialID));
	const people = useSelector(selectPeople);
	const isCredentialLoading = !!useSelector((state) => selectCredentialLoading(state, credentialID));

	const [internalVisible, internalSetVisible] = useState(false);
	const visible = open !== undefined ? open : internalVisible;
	const setVisible = setOpen !== undefined ? setOpen : internalSetVisible;

	const [initialPersonID, setInitialPersonID] = useState(null);
	const [initialValidity, setInitialValidity] = useState({ type: 'permanent', dates: null });

	const [validityType, setValidityType] = useState('permanent');
	const [durationMode, setDurationMode] = useState(null); // 'duration' | 'custom'
	const [durationAmount, setDurationAmount] = useState(1);
	const [durationUnit, setDurationUnit] = useState('week');
	const [dateRange, setDateRange] = useState(null);

	const rangePickerRef = useRef(null);

	const DURATION_UNITS = [
		{ label: 'Days', value: 'day' },
		{ label: 'Weeks', value: 'week' },
		{ label: 'Months', value: 'month' },
		{ label: 'Years', value: 'year' }
	];

	const applyDuration = (amount, unit) => {
		const start = dayjs();
		const end = start.add(amount, unit);
		setDateRange([start, end]);
	};

	const personOptions = people.map((p) => ({
		value: p.personID,
		label: p.displayName || `${p.firstName || ''} ${p.lastName || ''}`.trim() || p.email
	}));

	useEffect(() => {
		if (visible && credential) {
			const currentPerson = credential.personID || null;
			setInitialPersonID(currentPerson);
			form.setFieldsValue({ personID: currentPerson });

			let isPermanent = true;
			let parsedStart = null;
			let parsedEnd = null;

			const isZeroDate = (d) => !d || (typeof d === 'string' && d.startsWith('0001-01-01'));

			if (credential.startDate && credential.endDate) {
				const start = dayjs(credential.startDate);
				const end = dayjs(credential.endDate);

				if (!isZeroDate(credential.startDate) && !isZeroDate(credential.endDate) && start.isValid() && end.isValid()) {
					isPermanent = false;
					parsedStart = start;
					parsedEnd = end;
				}
			}

			const initialType = isPermanent ? 'permanent' : 'time-based';
			const initialDates = isPermanent ? null : [parsedStart, parsedEnd];

			setInitialValidity({ type: initialType, dates: initialDates });
			setValidityType(initialType);
			setDurationMode(isPermanent ? null : 'custom');
			setDateRange(initialDates);
		}
	}, [visible, credential, form]);

	const handleCancel = () => {
		form.resetFields();
		setValidityType('permanent');
		setDurationMode(null);
		setDurationAmount(1);
		setDurationUnit('week');
		setDateRange(null);
		setVisible(false);
	};

	const handleFinish = async () => {
		try {
			const values = await form.validateFields();
			const currentPersonID = values.personID || null;

			let validityChanged = false;
			let payload = { startDate: null, endDate: null };

			if (validityType === 'time-based' && dateRange && dateRange.length === 2) {
				payload = {
					startDate: dateRange[0].toISOString(),
					endDate: dateRange[1].toISOString()
				};

				const initialPayload = {
					startDate: initialValidity.dates?.[0]?.toISOString() || null,
					endDate: initialValidity.dates?.[1]?.toISOString() || null
				};

				if (validityType !== initialValidity.type || !isEqual(payload, initialPayload)) {
					validityChanged = true;
				}
			} else if (validityType === 'permanent') {
				if (initialValidity.type !== 'permanent') {
					validityChanged = true;
				}
			}

			const personChanged = currentPersonID !== initialPersonID;

			if (!validityChanged && !personChanged) {
				handleCancel();
				return;
			}

			dispatch(
				credentialsActions.updateCredential({
					credentialID,
					data: {
						validityChanged,
						personChanged,
						startDate: validityType === 'permanent' ? null : dateRange[0]?.toISOString(),
						endDate: validityType === 'permanent' ? null : dateRange[1]?.toISOString(),
						newPersonID: currentPersonID,
						oldPersonID: initialPersonID
					}
				})
			);

			handleCancel();
		} catch (error) {
			console.error('Validation failed:', error);
		}
	};

	const isSubmitDisabled = validityType === 'time-based' && (!dateRange?.[0] || !dateRange?.[1]);

	return (
		<>
			<Tooltip title='Update Credential'>
				{children ? (
					<Flex style={{ height: '100%', cursor: 'pointer' }} onClick={() => setVisible(true)}>
						{children}
					</Flex>
				) : (
					<Button onClick={() => setVisible(true)}>Update Credential</Button>
				)}
			</Tooltip>

			<Modal
				open={visible}
				onCancel={handleCancel}
				width={500}
				centered
				closeIcon={false}
				destroyOnClose
				styles={{
					content: { padding: 0 },
					body: { padding: paddingXL },
					footer: {
						padding: paddingXL,
						borderTop: `1px solid ${colorBorderSecondary}`
					}
				}}
				footer={[
					<Flex gap={paddingSM} align='center' justify='flex-end' key='footer-actions'>
						<Button key='cancel' onClick={handleCancel} disabled={isCredentialLoading}>
							Cancel
						</Button>
						<Button
							type='primary'
							onClick={handleFinish}
							key='submit'
							disabled={isSubmitDisabled}
							loading={isCredentialLoading}
						>
							Save Changes
						</Button>
					</Flex>
				]}
			>
				<Title level={4} style={{ margin: 0, marginBottom: marginXS }}>
					Update Credential
				</Title>
				<Text style={{ lineHeight: '20px', fontSize: fontSizeSM, color: colorTextDescription }}>
					Manage who this credential is assigned to and define its validity period.
				</Text>

				<Form form={form} layout='vertical' style={{ marginTop: marginLG }}>
					<Flex vertical gap={marginXS} style={{ marginBottom: marginLG }}>
						<Text strong>Assignment</Text>
						<Flex
							vertical
							gap={paddingSM}
							style={{ background: colorBgLayout, padding: paddingSM, borderRadius: 8 }}
						>
							<Form.Item
								name='personID'
								label='Assigned Person'
								style={{ fontWeight: fontWeightStrong, margin: 0 }}
							>
								<Select
									options={personOptions}
									placeholder='Select a person to assign...'
									showSearch
									allowClear
									filterOption={(input, option) =>
										(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
									}
									style={{ fontWeight: 400 }}
								/>
							</Form.Item>
							<Text type='secondary' style={{ fontSize: 11 }}>
								Clear the selection to unassign this credential.
							</Text>
						</Flex>
					</Flex>

					<Flex vertical gap={marginXS}>
						<Text strong>Access Validity</Text>
						<Flex
							vertical
							gap={paddingSM}
							style={{ background: colorBgLayout, padding: paddingSM, borderRadius: 8 }}
						>
							<Form.Item label='Type' style={{ fontWeight: fontWeightStrong, margin: 0 }}>
								<Radio.Group
									value={validityType}
									onChange={(e) => {
										const newType = e.target.value;
										setValidityType(newType);
										if (newType === 'permanent') {
											setDurationMode(null);
											setDateRange(null);
										} else {
											setDurationMode('duration');
											applyDuration(durationAmount, durationUnit);
										}
									}}
									style={{ fontWeight: 400 }}
								>
									<Radio value='permanent'>Permanent</Radio>
									<Radio value='time-based'>Time-based</Radio>
								</Radio.Group>
							</Form.Item>

							{validityType === 'time-based' && (
								<>
									<Form.Item label='Duration' style={{ fontWeight: fontWeightStrong, margin: 0 }}>
										<Radio.Group
											value={durationMode}
											onChange={(e) => {
												const mode = e.target.value;
												setDurationMode(mode);
												if (mode === 'duration') {
													applyDuration(durationAmount, durationUnit);
												} else {
													setDateRange([dayjs(), null]);
													setTimeout(() => {
														rangePickerRef.current?.focus();
													}, 150);
												}
											}}
											style={{ fontWeight: 400 }}
										>
											<Radio value='duration'>Duration</Radio>
											<Radio value='custom'>Custom</Radio>
										</Radio.Group>
									</Form.Item>

									{durationMode === 'duration' && (
										<>
											<Flex gap={paddingSM}>
												<InputNumber
													min={1}
													max={99}
													value={durationAmount}
													onChange={(val) => {
														const amount = val || 1;
														setDurationAmount(amount);
														applyDuration(amount, durationUnit);
													}}
													style={{ width: 80, fontWeight: 400 }}
												/>
												<Select
													value={durationUnit}
													onChange={(unit) => {
														setDurationUnit(unit);
														applyDuration(durationAmount, unit);
													}}
													options={DURATION_UNITS}
													style={{ flex: 1, fontWeight: 400 }}
												/>
											</Flex>
											{dateRange?.[0] && dateRange?.[1] && (
												<Text type='secondary' style={{ fontSize: 12 }}>
													{dateRange[0].format('DD/MM/YYYY hh:mm A')} —{' '}
													{dateRange[1].format('DD/MM/YYYY hh:mm A')}
												</Text>
											)}
										</>
									)}

									{durationMode === 'custom' && (
										<Flex vertical gap={paddingSM}>
											<Form.Item
												label='Start Date'
												style={{ fontWeight: fontWeightStrong, margin: 0 }}
											>
												<DatePicker
													value={dateRange?.[0] || null}
													onChange={(date) => setDateRange([date, dateRange?.[1] || null])}
													format='DD/MM/YYYY hh:mm A'
													showTime
													disabledDate={(current) =>
														current && current.isBefore(dayjs(), 'day')
													}
													disabledTime={(current) => {
														if (!current || !current.isSame(dayjs(), 'day')) return {};
														const now = dayjs();
														return {
															disabledHours: () =>
																Array.from({ length: 24 }, (_, i) => i).filter(
																	(h) => h < now.hour()
																),
															disabledMinutes: (selectedHour) =>
																selectedHour === now.hour()
																	? Array.from({ length: 60 }, (_, i) => i).filter(
																			(m) => m < now.minute()
																	  )
																	: []
														};
													}}
													style={{ width: '100%', fontWeight: 400 }}
													allowClear={false}
												/>
											</Form.Item>
											<Form.Item
												label='End Date'
												style={{ fontWeight: fontWeightStrong, margin: 0 }}
											>
												<DatePicker
													ref={rangePickerRef}
													value={dateRange?.[1] || null}
													onChange={(date) =>
														setDateRange([dateRange?.[0] || dayjs(), date])
													}
													format='DD/MM/YYYY hh:mm A'
													showTime
													disabledDate={(current) => {
														if (!current) return false;
														const start = dateRange?.[0] || dayjs();
														return current.isBefore(start, 'day');
													}}
													disabledTime={(current) => {
														const start = dateRange?.[0] || dayjs();
														if (!current || !current.isSame(start, 'day')) return {};
														return {
															disabledHours: () =>
																Array.from({ length: 24 }, (_, i) => i).filter(
																	(h) => h < start.hour()
																),
															disabledMinutes: (selectedHour) =>
																selectedHour === start.hour()
																	? Array.from({ length: 60 }, (_, i) => i).filter(
																			(m) => m <= start.minute()
																	  )
																	: []
														};
													}}
													style={{ width: '100%', fontWeight: 400 }}
													allowClear={false}
												/>
											</Form.Item>
										</Flex>
									)}
								</>
							)}
						</Flex>
					</Flex>
				</Form>
			</Modal>
		</>
	);
};

export default UpdateCredentialModal;
